(ns ycweb.app.admin.tech.system.featureflag
  (:require
   [clojure.string :as str]
   [rum.core :as rum]
   [ycweb.app.dashboard.common :as dcommon]
   [ycweb.app.dashboard.presence :as presence]
   [ycweb.components.common :as cmn]
   [ycweb.components.datatables.datatables :as datatables]
   [ycweb.components.editor.base :as editor]
   [ycweb.components.flow :as flow]
   [ycweb.components.headings :as headings]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.transitions :as t]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as yuc]
   [ycweb.util.urlstate :as urlstate]))

(def datatable-key :admin.feature-flag/list)

(def editor-key :admin.feature-flag/editor)

(def create-editor-key :admin.feature-flag/create-editor)

(def editor-config
  {:editor-name editor-key
   :datatable datatable-key
   :mode :read
   :end-points {:edit :admin.feature-flag/edit}
   :fields
   [{:label "Threshold %:"
     :name :feature-flag/value
     :attr
     {:hint
      [:div
       [:p "0 - feature flag disabled"]
       [:p "100 - feature flag enabled"]
       [:p "anywhere in-between is a chance it will be used"]]}}
    {:label "Active:"
     :name :feature-flag/active
     :type :checkbox}
    {:label "Retention Days:"
     :name :feature-flag/retention-days}
    {:label "Reporting Mode:"
     :name :feature-flag/reporting-mode
     :type :select
     :options
     [{:value "on" :text "On (always report)"}
      {:value "off" :text "Off (never report)"}
      {:value "allowed" :text "Allowed (only when flag passes)"}
      {:value "declined" :text "Declined (only when flag does not pass)"}]}]})

(def create-editor-config
  {:editor-name create-editor-key
   :datatable datatable-key
   :mode :create
   :end-points {:create :admin.feature-flag/create}
   :fields
   [{:label "Feature Key:"
     :name :feature-key
     :attr {:placeholder "e.g. grouping/my-feature"}}
    {:label "Threshold %:"
     :name :value
     :attr
     {:hint
      [:div
       [:p "0 - feature flag disabled"]
       [:p "100 - feature flag enabled"]
       [:p "anywhere in-between is a chance it will be used"]]}}]})

(defmethod comms/receive :admin.feature-flag/edit [app-atom e]
  (let [{:keys [success]} (editor/post-submit app-atom editor-key e)]
    (when success
      (datatables/update-rows app-atom datatable-key e))))

(defmethod comms/receive :admin.feature-flag/create [app-atom e]
  (editor/post-submit app-atom create-editor-key e)
  (when (:success e)
    (datatables/overlay app-atom :close datatable-key)
    (comms/send :admin.feature-flag/list {})))

;; --- Datatable ---

(def datatable-fns
  {:column-fns
   {:feature-key-link-fn
    (fn [app-atom row _ v]
      [:a
       (urlstate/href-props app-atom "featureflag" "overview"
         {:uuid (:feature-flag/uuid row)})
       [:span {:style {:cursor "pointer"}} v]])
    :groups-fn
    (fn [_ row _ _]
      (let [text (:feature-flag/display-groups row)]
        (cond
          (str/blank? text) [:span {:style {:color "#999"}} "—"]
          :else text)))
    :metadata-fn
    (fn [_ row _ _]
      (if (str/blank? (:feature-flag/metadata row)) "No" "Yes"))
    :active-fn
    (fn [_ row _ _]
      (if (:feature-flag/active row) "Yes" "No"))}})

(def datatable-columns
  [{:title "Name"
    :key :feature-flag/feature-key
    :render {:render-k :feature-key-link-fn}
    :options {:ignore-row-click true}}
   {:title "Groups"
    :key :feature-flag/display-groups
    :render {:render-k :groups-fn}}
   {:title "Probability %"
    :key :feature-flag/value
    :align-class :u-text-center
    :type :percentage}
   {:title "Active"
    :key :feature-flag/active
    :align-class :u-text-center
    :render {:render-k :active-fn}}
   {:title "Total Decisions Approved"
    :key :feature-flag/total-decision-approved
    :align-class :u-text-center}
   {:title "Total Decisions Reported"
    :key :feature-flag/total-decisions
    :align-class :u-text-center}
   {:title "Current Probability Capacity %"
    :key :feature-flag/total-decision-threshold
    :align-class :u-text-center
    :type :percentage}
   {:title "Retention Days"
    :key :feature-flag/retention-days
    :align-class :u-text-center}
   {:title "Reporting Mode"
    :key :feature-flag/reporting-mode
    :align-class :u-text-center}
   {:title "Metadata"
    :key :feature-flag/metadata
    :align-class :u-text-center
    :render {:render-k :metadata-fn}}])

(defn datatable-config [app-atom]
  (let [action-name (cmn/process-action-name app-atom)]
    {:table-name datatable-key
     :row-id :feature-flag/id
     :data-source
     {:cursor [action-name :feature-flags]}
     :select? true
     :options
     {:order :asc
      :sticky-header? true
      :paging? true
      :pagination {:page 0 :page-size 20}}
     :buttons
     [{:text "Create Flag"
       :editor create-editor-key
       :extends :create}
      {:text "Update"
       :editor editor-key
       :extends :edit}]
     :column-defs datatable-columns}))

(rum/defc feature-flag-dashboard < rum/reactive cmn/cursors [app-atom]
  [:div
   [:div
    {:class (yuc/bem :flex-grid-group [:flush-force :align-left :valign-middle])}
    (headings/heading "Feature Flag Dashboard" {:type :h1 :size :large})]

   [:p.u-margin-top-8 {:style {:color "#666" :font-size "13px"}}
    "Click a flag name to open its overview, where you can manage groups, users, metadata, description, and view reports / history."]

   [:div.u-relative
    (datatables/table app-atom datatable-key datatable-fns)
    (toaster/loading-overlay app-atom :feature-flag-overlay)]])

(defmethod flow/render-screen :admin.featureflag [app-atom _]
  (feature-flag-dashboard app-atom))

(defmethod t/transition-after-open :admin.featureflag [app-atom _ _]
  (dcommon/kill-search-bar)
  (presence/open app-atom {:context/ident :admin.featureflag})
  (datatables/initiate app-atom (datatable-config app-atom) datatable-fns)
  (editor/initiate app-atom editor-config)
  (editor/initiate app-atom create-editor-config)
  (datatables/overlay app-atom :open datatable-key)
  (comms/send :admin.feature-flag/list {}))

(defmethod comms/receive :admin.feature-flag/list [app-atom {:keys [rows]}]
  (datatables/overlay app-atom :close datatable-key)
  (datatables/add-rows app-atom datatable-key {:rows rows}))
